import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 sm:p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>

          <p className="text-gray-600 mb-6">Last updated: August 8, 2025</p>

          <div className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-600 mb-4">
                This Privacy Policy describes how Postgame Buzz (“we,” “us,” or “our”) collects, uses, and shares information when you use our website and services. We serve users worldwide, with primary operations in the United States.
              </p>
              <p className="text-gray-600 mb-4">
                By using our services, you agree to this Policy. If you do not agree, please do not use the services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li><strong>Account Information:</strong> When you sign in, we receive basic profile information from your sign‑in provider (e.g., name and email).</li>
                <li><strong>Content You Provide:</strong> Uploaded files (e.g., box scores), team and roster details (including player names, numbers, and positions), titles/descriptions, and other inputs necessary to generate podcasts.</li>
                <li><strong>Transactional Information:</strong> Limited order information from our payment provider (e.g., purchaser name and email). We do not collect or store full payment card numbers.</li>
                <li><strong>Usage and Device Information:</strong> Basic technical data such as IP address, browser type, pages viewed, and interactions, used to operate and secure the services.</li>
                <li><strong>Cookies and Similar Technologies:</strong> We use essential session cookies to keep you signed in and secure your account. We do not currently use analytics or advertising cookies, but may enable analytics in the future (see “Analytics” below).</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">How We Use Information</h2>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li>Provide, operate, and improve the services.</li>
                <li>Generate AI‑produced audio based on your inputs and uploaded content.</li>
                <li>Authenticate users and maintain account security.</li>
                <li>Process orders and manage credits and access.</li>
                <li>Debug, monitor performance, and protect against fraud or abuse.</li>
                <li>Comply with applicable law and enforce our Terms.</li>
              </ul>
              <p className="text-gray-600 mb-4">
                <strong>AI output caution:</strong> AI‑generated content can be unpredictable and may contain inaccuracies. You are responsible for the content you upload and generate. We may review or remove content that we reasonably believe is unlawful or violates our terms or community standards.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Analytics</h2>
              <p className="text-gray-600 mb-4">
                We do not currently use analytics tools that set non‑essential cookies. If we enable analytics in the future (for example, Google Analytics), we will update this Policy and, where required, provide appropriate notices or obtain any necessary consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">How We Share Information</h2>
              <p className="text-gray-600 mb-4">
                We share information in limited circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                <li><strong>Service Providers:</strong> We use vendors to provide authentication, payment processing, content processing and generation, cloud storage and hosting, security, and customer support. These providers process data on our behalf under appropriate safeguards.</li>
                <li><strong>Legal and Safety:</strong> To comply with law, respond to legal requests, or protect rights, safety, and property.</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, financing, or sale of assets, subject to appropriate safeguards.</li>
                <li><strong>With Your Direction:</strong> When you ask us to share or make your content available.</li>
              </ul>
              <p className="text-gray-600 mb-4">
                We do not sell personal information and we do not share personal information for cross‑context behavioral advertising.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Storage, Security, and International Transfers</h2>
              <p className="text-gray-600 mb-4">
                We store data in the United States and may process it in other countries where our service providers operate. We use reasonable administrative, technical, and physical safeguards, including encryption in transit (TLS) and at rest where supported by our providers, private cloud storage, and authenticated, time‑limited access links for audio streaming. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Retention and Deletion</h2>
              <p className="text-gray-600 mb-4">
                We retain information for as long as needed to provide the services and for legitimate business purposes (such as security, fraud prevention, and legal compliance). You can delete your podcasts at any time from within the product’s “My Podcasts” area. When you delete a podcast, we remove the database record and we attempt to delete the associated audio file from our cloud storage. If deletion from storage fails due to a provider issue, we will continue attempts or apply alternative safeguards.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Children and Youth Sports Content</h2>
              <p className="text-gray-600 mb-4">
                Our service is intended for adults (such as parents or coaches). We do not knowingly allow children under 13 to create accounts. Content you upload may include names or information about minors involved in youth sports. By providing such content, you represent that you have the necessary rights and permissions to do so, and that you will not upload sensitive data beyond what is reasonably necessary for creating the podcast.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Privacy Rights</h2>
              <p className="text-gray-600 mb-4">
                Depending on your location, you may have rights under laws such as the GDPR/UK GDPR and California privacy laws (CCPA/CPRA), including rights to access, correct, delete, or port certain data, and to restrict or object to certain processing. You can manage certain data directly in the product (e.g., delete podcasts). Additional mechanisms may be provided within the service for other requests, subject to verification.
              </p>
              <p className="text-gray-600 mb-4">
                We do not use personal information for targeted advertising, nor do we sell personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookies</h2>
              <p className="text-gray-600 mb-4">
                We use essential session cookies to maintain your login and keep the service secure. Because these cookies are strictly necessary, they do not require consent. If we later introduce analytics or marketing cookies, we will provide appropriate notice and controls.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-600 mb-4">
                We may update this Policy from time to time. When we do, we will post the updated version here and update the “Last updated” date above. Material changes may be accompanied by additional notice within the product.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
